// Calls to the super admin console API (vTrackApp api/v1/super-admin). Every endpoint there is
// super-admin only on the server; a 403 means the signed-in user isn't a super admin.
// Writes (client edit/status/features, device suspend) reuse ../SuperAdmin/services.js unchanged;
// the two deletes are below.

import axios from "axios";
import * as Constants from "../../utils/Constants";
import {getAuthToken} from "../../utils/utility";
import {extractPaginationHeaders} from "../../utils/paginationUtils";

const BASE = `${Constants.BASE_URL}${Constants.API_V1}/super-admin`;

const authConfig = (params, signal) => ({
  params,
  signal,
  headers: {Authorization: `${Constants.BEARER} ${getAuthToken()}`},
});

const page = (res) => {
  const rows = res.data?.responseData || [];
  const paging = extractPaginationHeaders(res);
  return {rows, total: paging ? paging.totalCount : rows.length};
};

export const fetchOverview = async (signal) =>
  (await axios.get(`${BASE}/overview`, authConfig(undefined, signal))).data?.responseData;

export const fetchClients = async (params, signal) =>
  page(await axios.get(`${BASE}/clients`, authConfig(params, signal)));

export const fetchClientOptions = async (signal) =>
  (await axios.get(`${BASE}/clients/options`, authConfig(undefined, signal))).data?.responseData || [];

export const fetchClient = async (clientId, signal) =>
  (await axios.get(`${BASE}/clients/${clientId}`, authConfig(undefined, signal))).data?.responseData;

export const fetchUsers = async (clientId, params, signal) =>
  page(await axios.get(`${BASE}/clients/${clientId}/users`, authConfig(params, signal)));

export const fetchDevices = async (params, signal) =>
  page(await axios.get(`${BASE}/devices`, authConfig(params, signal)));

export const fetchDevice = async (assetId, signal) =>
  (await axios.get(`${BASE}/devices/${assetId}`, authConfig(undefined, signal))).data?.responseData;

/** True when a request failed because the server refused access (not a super admin). */
/**
 * Soft-deletes a device (vTrackApp AssetService: assignments returned, IMEI free to register
 * again, history kept). The server takes the caller from the login token.
 */
export const deleteDevice = async (clientId, assetId) =>
  (
    await axios.delete(
      `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/assets/${assetId}`,
      authConfig()
    )
  ).data;

/**
 * Soft-deletes a client with its devices and users (vTrackApp ClientDeletionService). confirm is
 * the client's name, which the server checks again.
 */
export const deleteClient = async (clientId, confirm) =>
  (
    await axios.delete(`${Constants.BASE_URL}${Constants.API_V1}/clients/${clientId}`, authConfig({confirm}))
  ).data?.responseData;

export const isAccessDenied = (error) => error?.response?.status === 403;

/** True when a request was cancelled because a newer one replaced it. */
export const isCancelled = (error) => axios.isCancel(error) || error?.name === "CanceledError";
